import React from 'react';
import { Form } from 'react-bootstrap';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from 'components/common/DatePicker';
import ExpandingTextArea from 'react-expanding-textarea';

// Services & Helpers
import BaseService from 'services/BaseService';
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';
import VendorService from 'services/VendorService';
import CourseService from 'services/CourseService';
import CertService from 'services/CertService';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import CourseTier from 'components/common/CourseTier';
import EmailSettingsModal from 'components/instructor/EmailSettingsModal';
import NotesTable from 'components/notes/NotesTable';

//-------------------------------------------------------------------------------------------------------------------

class Credentials extends React.Component {

    constructor(props) {
        super(props);

        this.emailSettingsModalRef = React.createRef();

        this.state = {
            isLoading: true,
            vendorID: null,
            notesVisible: {},
            isUploadingEvidence: {},
            emailSettingsLookup: {}
        };
    }

    componentDidMount() {
        this.load();
    }

    async load() {
        const instructor = await BaseService.callAPI('instructor/get-credentials/' + this.props.instructor.id);
        const courses = await CourseService.list();
        const certs = await CertService.list();
        const vendors = await VendorService.list();
        this.loadEmailSettings();
        this.setState({
            instructor,
            courses,
            certs,
            vendors,
            isLoading: false
        });
    }

    async loadEmailSettings() {
        const emailSettings = await InstructorService.getAllEmailSettings(this.props.instructor.id);
        const emailSettingsLookup = {};
        emailSettings.forEach(setting => {
            let key;
            if (setting.courseID) key = `course-${setting.courseID}`;
            else if (setting.certID) key = `cert-${setting.certID}`;
            if (!emailSettingsLookup[key]) {
                emailSettingsLookup[key] = {
                    numSkipped: 0
                };
            }
            if (setting.behaviour == 'Skip') {
                emailSettingsLookup[key].numSkipped++;
            }
        });
        this.setState({
            emailSettings,
            emailSettingsLookup
        });
    }

    async save() {
        const { setIsBusy, setIsChanged } = this.props;
        const { instructor } = this.state;
        setIsBusy(true);
        this.setState({ isLoading: true });
        await BaseService.callAPI('instructor/save-credentials', instructor);
        setIsBusy(false);
        setIsChanged(false);
        this.setState({ isLoading: false });
        BootboxHelper.alert('Changes saved');
        //this.load();
    }

    updateFields(values) {
        const instructor = { ...this.state.instructor };
        for (var fieldName in values) {
            const value = values[fieldName];
            instructor[fieldName] = value;
        }
        this.setState({ instructor });
        this.props.setIsChanged(true, () => this.save());
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Courses
    //-------------------------------------------------------------------------------------------------------------------

    startAddingCourse() {
        this.setState({ isAddingCourse: true })
    }

    addCourse(course) {
        const courses = [... this.state.instructor.courses];
        const instructorCourse = {
            id: courses.length - 1,
            courseID: course.id,
            courseName: course.name,
            courseTier: course.tier,
            course,
            isApproved: true,
            isNew: true
        };
        courses.push(instructorCourse);
        this.updateFields({ courses });
        this.setState({ isAddingCourse: false });
    }

    updateCourseFields(courseID, values) {
        const courses = [...this.state.instructor.courses];
        const index = courses.findIndex(c => c.courseID == courseID);
        const instructorCourse = { ...courses[index] };
        for (var field in values) {
            instructorCourse[field] = values[field];
        }
        courses[index] = instructorCourse;
        this.updateFields({ courses });
    }

    async confirmDeleteCourse(courseID, name) {
        const confirm = await BootboxHelper.confirm(`Are you sure you want to remove <b>${name}<b/>?`);
        if (confirm) {
            this.deleteCourse(courseID);
        }
    }

    async deleteCourse(courseID) {
        const courses = [... this.state.instructor.courses];
        const index = courses.findIndex(c => c.courseID == courseID);
        courses.splice(index, 1);
        this.updateFields({
            courses: courses
        });
        if (this.state.instructor.id) {
            InstructorService.deleteCourse(this.state.instructor.id, courseID);
        }
    }

    toggleNotes(courseID) {
        const notesVisible = { ...this.state.notesVisible };
        if (notesVisible[courseID]) {
            delete notesVisible[courseID];
        } else {
            notesVisible[courseID] = true;
            this.autoFocus = `course-notes-${courseID}`;
        }
        this.setState({ notesVisible });
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Ramping Courses
    //-------------------------------------------------------------------------------------------------------------------

    startAddingRampingCourse() {
        this.setState({
            isAddingRampingCourse: true
        })
    }

    addRampingCourse(course) {
        const courses = [... this.state.instructor.courses];
        const instructorCourse = {
            id: courses.length - 1,
            courseID: course.id,
            courseName: course.name,
            courseTier: course.tier,
            course,
            isNew: true
        };
        courses.push(instructorCourse);
        this.updateFields({ courses });
        this.setState({ isAddingRampingCourse: false });
    }

    async confirmSubmitCourseForApproval(courseID, name) {
        const confirm = await BootboxHelper.confirm(`Do you want to submit the course <b>${name}</b> to vendor for approval?`);
        if (confirm) {
            this.updateCourseFields(courseID, { dateSubmittedForApproval: new Date() });
            await InstructorService.submitCourseForApproval(this.state.instructor.id, courseID);
        }
    }

    async confirmApproveCourse(courseID, name) {
        const confirm = await BootboxHelper.confirm(`Do you want to approve the course <b>${name}</b>?`);
        if (confirm) {
            this.updateCourseFields(courseID, {
                isApproved: true,
                dateExamPassed: new Date()
            });
        }
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Certs
    //-------------------------------------------------------------------------------------------------------------------

    startAddingCert() {
        this.setState({
            isAddingCert: true
        });
    }

    addCert(cert) {
        const certs = [... this.state.instructor.certs];
        const instructorCert = {
            id: certs.length - 1,
            certID: cert.id,
            certName: cert.name,
            vendorID: cert.vendorID,
            isNew: true
        };
        certs.push(instructorCert);
        this.updateFields({ certs });
        this.setState({
            isAddingCert: false
        });
    }

    updateCertFields(certID, values) {
        const certs = [...this.state.instructor.certs];
        const index = certs.findIndex(c => c.certID == certID);
        const instructorCert = { ...certs[index] };
        for (var field in values) {
            instructorCert[field] = values[field];
        }
        certs[index] = instructorCert;
        this.updateFields({ certs });
    }

    async confirmDeleteCert(certID, name) {
        const confirm = await BootboxHelper.confirm(`Are you sure you want to remove <b>${name}<b/>?`);
        if (confirm) {
            this.deleteCert(certID);
        }
    }

    async deleteCert(certID) {
        const certs = [... this.state.instructor.certs];
        const index = certs.findIndex(c => c.certID == certID);
        certs.splice(index, 1);
        this.updateFields({ certs });
    }

    async uploadCertFile(courseID, certID, field) {
        if (field.files.length > 0) {
            const file = field.files[0];
            let type;
            if (courseID) {
                this.updateCourseFields(courseID, { isUploading: true });
                type = 'InstructorCourse';
            } else {
                this.updateCertFields(certID, { isUploading: true });
                type = 'InstructorCert';
            }
            var { guid } = await BaseService.postFile('upload/add', file, { type, instructorID: this.props.instructor.id });
            field.value = '';
            if (courseID) {
                this.updateCourseFields(courseID, { uploadGUID: guid, isUploading: false, isUploaded: true });
            } else {
                this.updateCertFields(certID, { uploadGUID: guid, isUploading: false, isUploaded: true });
            }
        }
    }

    async editCredlyLink(certID) {
        const instructorCert = this.state.instructor.certs.find(c => c.certID == certID);
        const url = await BootboxHelper.prompt('Enter Credly link', {
            value: instructorCert.url
        });
        if (url !== null) {
            this.updateCertFields(certID, { url });
        }
    }

    getCourseValidationMessages(ic) {
        const { certs, instructor } = this.state;
        const { isEditMode } = this.props;

        // Determine presence & validity of certs
        const messages = [];
        let certsNeeded = [];
        const checkCert = (id) => {
            const cert = certs.find(c => c.id == id);
            const instructorCert = instructor.certs.find(c => c.certID == id);
            const isValid = (
                instructorCert &&
                instructorCert.expiryDate &&
                !moment(instructorCert.expiryDate).isBefore(moment())
            );
            certsNeeded.push({
                cert,
                isValid
            });
        }
        if (isEditMode) {
            if (ic.course.certNeeded1ID) {
                checkCert(ic.course.certNeeded1ID);
            }
            if (ic.course.certNeeded2ID) {
                checkCert(ic.course.certNeeded2ID);
            }
            if (ic.course.certNeeded3ID) {
                checkCert(ic.course.certNeeded3ID);
            }
            if (certsNeeded.length > 0 && !certsNeeded.find(c => c.isValid)) {
                messages.push(`You need a valid ${certsNeeded.map(c => `'${c.cert.name}'`).join(' OR ')} certification to teach this course`);
            }
        }
        return messages;
    }

    async showEmailSettingsModal({ courseID, certID }) {
        await this.emailSettingsModalRef.current.show({
            instructorID: this.state.instructor.id,
            courseID,
            certID
        });
        this.loadEmailSettings();
    }

    //-------------------------------------------------------------------------------------------------------------------
    //  Render
    //-------------------------------------------------------------------------------------------------------------------

    render() {
        const { isLoading, vendorID } = this.state;
        if (isLoading) {
            return (<Loader />);
        }
        const vendors = [{ id: '', name: '(All)' }].concat(this.state.vendors);
        const loginInfo = GlobalStateService.getValue('loginInfo');

        return (<>

            {loginInfo.isAdmin &&
                <Form.Group className="mb-3">
                    <Form.Label>Vendor</Form.Label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        value={vendors.find(v => v.id == vendorID)}
                        options={vendors}
                        getOptionValue={v => v.id}
                        getOptionLabel={v => v.name}
                        onChange={v => this.setState({ vendorID: v.id })}
                        placeholder="(All)"
                    />
                </Form.Group>
            }

            {this.renderApprovedCourses()}
            {this.renderRampingCourses()}
            {this.renderCerts()}

            <EmailSettingsModal ref={this.emailSettingsModalRef} />
        </>);
    }

    renderApprovedCourses() {
        const { isAddingCourse, courses, instructor, vendorID, emailSettingsLookup, notesVisible } = this.state;
        const { isEditMode } = this.props;
        const approvedCourses = instructor.courses.filter(ic => ic.isApproved && (!vendorID || ic.course.vendorID == vendorID));
        const loginInfo = GlobalStateService.getValue('loginInfo');

        return (<>
            <h2>
                Courses Approved to Deliver
            </h2>

            <div className="sticky-table-container no-max-height-desktop">
                <table className="table table-bordered sticky-table courses-table">
                <thead>
                    <tr>
                        <th className="course-column">Course</th>
                        {loginInfo.isVendorOrAdmin &&
                            <th className="notes-column">Notes</th>
                        }
                        <th className="dig-column">DIG Cert</th>
                        <th className="date-approved-column">Approved on</th>
                        <th className="actions-column"></th>
                    </tr>
                </thead>
                <tbody>
                    {approvedCourses.map(ic => {
                        const settings = emailSettingsLookup[`course-${ic.courseID}`] || { numSkipped: 0 };
                        const messages = this.getCourseValidationMessages(ic);

                        return (
                            <React.Fragment key={ic.courseID}>
                                <tr className={(notesVisible[ic.courseID] ? 'notes-visible' : '') + ' ' + (messages.length > 0 ? 'expired' : '')}>
                                    <td className="course-column">
                                        {ic.courseName} <CourseTier tier={ic.courseTier} />
                                    
                                        {messages.length > 0 &&
                                            <div className="warning-messages">
                                                {messages.map((m, index) =>
                                                    <div key={index}>{m}</div>
                                                )}
                                            </div>
                                        }
                                    </td>
                                    {loginInfo.isVendorOrAdmin &&
                                        <td className="notes-column">
                                            <button className="btn btn-secondary" onClick={() => this.toggleNotes(ic.courseID)}>
                                                {ic.hasNotes ?
                                                    <span className="fa-regular fa-comment-dots" /> :
                                                    <span className="fa-regular fa-comment" />
                                                }
                                            </button>
                                        </td>
                                    }
                                    <td className="dig-column">
                                        {this.renderDIGCert(ic, isEditMode)}
                                    </td>
                                    <td className="date-approved-column">
                                        {isEditMode ?
                                            <DatePicker
                                                locale="en-gb"
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="?"
                                                selected={ic.dateExamPassed ? new Date(ic.dateExamPassed) : null}
                                                onChange={date => this.updateCourseFields(ic.courseID, { dateExamPassed: date })}
                                            /> :
                                            (ic.dateExamPassed ? moment(ic.dateExamPassed).format('DD/MM/YYYY') : '')
                                        }
                                    </td>
                                    {isEditMode &&
                                        <td className="actions-column">
                                            <button className="btn btn-sm btn-danger" onClick={e => this.confirmDeleteCourse(ic.courseID, ic.courseName)} title="Remove this course">
                                                <span className="fa-solid fa-trash"></span>
                                            </button>
                                            {loginInfo.isAdmin &&
                                                <button className={`btn btn-sm btn-secondary ${settings.numSkipped > 0 ? 'email-settings-btn-skipped' : ''}`} onClick={e => this.showEmailSettingsModal({ courseID: ic.courseID })} title={settings.numSkipped + ' email(s) skipped'}>
                                                    <span className="fa-solid fa-envelope"></span>
                                                </button>
                                            }
                                        </td>
                                    }
                                </tr>
                                {loginInfo.isVendorOrAdmin && isEditMode && notesVisible[ic.courseID] &&
                                    <tr className="notes-row">
                                        <td colSpan={5}>

                                            <NotesTable
                                                instructorID={instructor.id}
                                                courseID={ic.courseID}
                                                certID={0}
                                                showNewestFirst
                                                allowShowAll
                                                maxNum={5}
                                                noteTypes={['Certification']}
                                                newNoteType="Certification"
                                                onLoad={(notes) => 
                                                    this.updateCourseFields(ic.courseID, { hasNotes: notes.length > 0 })
                                                }
                                            />

                                        </td>
                                    </tr>
                                }
                            </React.Fragment>
                        );
                    })}
                    {isEditMode && loginInfo.isAdmin &&
                        <tr>
                            <td className="4">
                                {isAddingCourse ?
                                    <Select
                                        autoFocus
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isClearable={false}
                                        options={courses.filter(c =>
                                            (!vendorID || c.vendorID == vendorID) &&
                                            c.allowRamping &&
                                            !c.isDeprecated &&
                                            !instructor.courses.find(ic => ic.courseID == c.id
                                        ))}
                                        getOptionValue={c => c.id}
                                        getOptionLabel={c => {
                                            if (c.__isNew__) return c.label;
                                            return `${c.name} (${c.tier})`
                                        }}
                                        onChange={c => this.addCourse(c)}
                                        value={null}
                                        menuIsOpen
                                        onBlur={e => this.setState({ isAddingCourse: false })}
                                    /> :
                                    <button className="btn btn-secondary btn-block" onClick={e => this.startAddingCourse()}>
                                        <span className="fa-solid fa-plus"></span>{' '}
                                        Add another...
                                    </button>
                                }
                            </td>
                        </tr>
                    }
                </tbody>
                </table>
            </div>
        </>);
    }

    renderDIGCert(ic, isEditMode) {
        if (!ic.course.isDIGRequired) {
            return 'No DIG required';
        }

        return (<>

            {!!ic.uploadGUID &&
                <a href={`/api/upload/get/${ic.uploadGUID}?isInline=true`} target="_blank" className="btn btn-secondary btn-sm download-button">
                    <span className="fa-solid fa-eye" />{' '}
                    View
                </a>
            }

            {isEditMode &&
                <label className="btn btn-sm btn-secondary upload-button">
                    {ic.isUploading ? <>
                        <Loader isInline />{' '}Uploading...
                    </> : <>
                        <span className="fa-solid fa-upload"></span>{' '}Upload PDF
                        <input
                            type="file"
                            accept="application/pdf"
                            onChange={e => this.uploadCertFile(ic.courseID, null, e.target)}
                        />
                    </>}
                </label>
            }

        </>);
    }

    renderRampingCourses() {
        const {
            isAddingRampingCourse,
            notesVisible,
            instructor,
            courses,
            vendorID,
            emailSettingsLookup
        } = this.state;
        const { loginInfo, isEditMode } = this.props;
        const rampingCourses = instructor.courses.filter(ic => !ic.isApproved && (!vendorID || ic.course.vendorID == vendorID));

        if (rampingCourses.length == 0 && !isEditMode) {
            return null;
        }

        setTimeout(() => this.autoFocus = null);

        return (<>
            <h2>Courses in Ramping</h2>
            {isEditMode &&
                <p className="detail-help">
                    Please select courses which you are currently studying to deliver and enter an estimated completion date.
                </p>
            }

            <div className="sticky-table-container no-max-height-desktop mb-3">
                <table className="table table-bordered sticky-table courses-table">
                <thead>
                    <tr>
                        <th className="course-column">Course</th>
                        <th className="est-completion-column">Est. completion</th>
                        {loginInfo.isVendorOrAdmin &&
                            <th className="notes-column">Notes</th>
                        }
                        <th className="dig-column">DIG Cert</th>
                        <th className="actions-column"></th>
                    </tr>
                </thead>
                <tbody>
                    {rampingCourses.map(ic => {
                        const messages = this.getCourseValidationMessages(ic);
                        const settings = emailSettingsLookup[`course-${ic.courseID}`] || { numSkipped: 0 };
                        
                        return (
                            <React.Fragment key={ic.courseID}>
                                <tr className={(notesVisible[ic.courseID] ? 'notes-visible' : '') + ' ' + (messages.length > 0 ? 'expired' : '')}>
                                    <td className="course-column">
                                        {ic.courseName}
                                        <CourseTier tier={ic.courseTier} />

                                        {messages.length > 0 &&
                                            <div className="warning-messages">
                                                {messages.map((m, index) =>
                                                    <div key={index}>{m}</div>
                                                )}
                                            </div>
                                        }
                                    </td>
                                    <td className="est-completion-column">
                                        {isEditMode ? <>
                                            <DatePicker
                                                locale="en-gb"
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="?"
                                                selected={ic.dateExamPassed ? new Date(ic.dateExamPassed) : null}
                                                onChange={date => this.updateCourseFields(ic.courseID, { dateExamPassed: date })}
                                            />
                                        </> : <>
                                            {!!ic.dateExamPassed && moment(ic.dateExamPassed).format('DD/MM/YYYY')}
                                        </>}
                                    </td>
                                    {loginInfo.isVendorOrAdmin &&
                                        <td className="notes-column">
                                            <button className="btn btn-secondary" onClick={() => this.toggleNotes(ic.courseID)}>
                                                {ic.hasNotes ?
                                                    <span className="fa-regular fa-comment-dots" /> :
                                                    <span className="fa-regular fa-comment" />
                                                }
                                            </button>
                                        </td>
                                    }
                                    <td className="dig-column">

                                        {this.renderDIGCert(ic, isEditMode)}

                                    </td>
                                    {isEditMode &&
                                        <td className="actions-column">
                                            {loginInfo.isAdmin && !ic.isNew && <>
                                                {ic.dateSubmittedForApproval ?
                                                    <>
                                                        <button className="btn btn-secondary btn-s me-1" style={{ borderColor: 'transparent', color: 'green' }}>
                                                            <span className="fa-solid fa-graduation-cap"></span>{' '}
                                                            Submitted on {moment(ic.dateSubmittedForApproval).format('DD/MM/YYYY')}
                                                        </button>
                                                        <button className="btn btn-secondary btn-sm me-1" onClick={e => this.confirmApproveCourse(ic.courseID, ic.courseName)}>
                                                            <span className="fa-solid fa-check"></span>{' '}
                                                            Approve
                                                        </button>
                                                    </> :
                                                    <button className="btn btn-sm btn-secondary me-1" onClick={e => this.confirmSubmitCourseForApproval(ic.courseID, ic.courseName)}>
                                                        <span className="fa-solid fa-graduation-cap"></span>{' '}
                                                        Submit for Approval
                                                    </button>
                                                }
                                            </>}
                                            <button className="btn btn-sm btn-danger" onClick={e => this.confirmDeleteCourse(ic.courseID, ic.courseName)} title="Remove this course">
                                                <span className="fa-solid fa-trash"></span>
                                            </button>

                                            {loginInfo.isAdmin &&
                                                <button className={`btn btn-sm btn-secondary ${settings.numSkipped > 0 ? 'email-settings-btn-skipped' : ''}`} onClick={e => this.showEmailSettingsModal({ courseID: ic.courseID })} title={settings.numSkipped + ' email(s) skipped'}>
                                                    <span className="fa-solid fa-envelope"></span>
                                                </button>
                                            }
                                        </td>
                                    }
                                </tr>
                                {loginInfo.isVendorOrAdmin && isEditMode && notesVisible[ic.courseID] &&
                                    <tr className="notes-row">
                                        <td colSpan={5}>
                                            
                                            <NotesTable
                                                instructorID={instructor.id}
                                                courseID={ic.courseID}
                                                certID={0}
                                                showNewestFirst
                                                allowShowAll
                                                maxNum={5}
                                                noteTypes={['Certification']}
                                                newNoteType="Certification"
                                                onLoad={(notes) =>
                                                    this.updateCourseFields(ic.courseID, { hasNotes: notes.length > 0 })
                                                }
                                            />

                                        </td>
                                    </tr>
                                }
                            </React.Fragment>
                        );
                    })}
                    {isEditMode &&
                        <tr>
                            <td className="4">
                                {isAddingRampingCourse ?
                                    <Select
                                        autoFocus
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        isClearable={false}
                                        options={courses.filter(c =>
                                            (!vendorID || c.vendorID == vendorID) &&
                                            c.allowRamping &&
                                            !c.isDeprecated &&
                                            !instructor.courses.find(ic => ic.courseID == c.id)
                                        )}
                                        getOptionValue={c => c.id}
                                        getOptionLabel={c => {
                                            if (c.__isNew__) return c.label;
                                            return `${c.name} (${c.tier})`
                                        }}
                                        onChange={c => this.addRampingCourse(c)}
                                        value={null}
                                        menuIsOpen
                                        onBlur={e => this.setState({ isAddingRampingCourse: false })}
                                    /> :
                                    <button className="btn btn-secondary btn-block" onClick={e => this.startAddingRampingCourse()}>
                                        <span className="fa-solid fa-plus"></span>{' '}
                                        Add another...
                                    </button>
                                }
                            </td>
                        </tr>
                    }
                </tbody>
                </table>
            </div>
        </>);
    }

    renderCerts() {
        const {
            isAddingCert,
            instructor,
            vendorID,
            emailSettingsLookup
        } = this.state;
        const {
            isEditMode
        } = this.props;

        const loginInfo = GlobalStateService.getValue('loginInfo');
        const certs = this.state.certs.filter(c => !vendorID || c.vendorID == vendorID);
        const instructorCerts = instructor.certs.filter(ic => !vendorID || ic.vendorID == vendorID);

        const getCertRowClass = (ic) => {
            if (!ic.expiryDate || moment(ic.expiryDate).isBefore(moment())) {
                return 'expired';
            }
            return '';
        };

        if (instructorCerts.length > 0 || isEditMode) {
            return (
                <div className="detail edit-mode-top-border">
                    <h2>Certifications</h2>

                    <table className="table table-bordered certs-table">
                        <thead>
                            <tr>
                                <th className="cert-column">Certification</th>
                                <th className="expiry-date-column">Expires on</th>
                                <th className="dig-column" colSpan="2">Certificate of completion</th>
                            </tr>
                        </thead>
                        <tbody>
                            {instructorCerts.map(ic => {
                                const settings = emailSettingsLookup[`cert-${ic.certID}`] || { numSkipped: 0 };

                                return (
                                    <tr key={ic.certID} className={getCertRowClass(ic)}>
                                        <td className="cert-column">
                                            {ic.certName}

                                            {!ic.expiryDate &&
                                                <div className="warning-messages">
                                                    Please provide an expiry date
                                                </div>
                                            }
                                            {ic.expiryDate && moment(ic.expiryDate).isBefore(moment()) &&
                                                <div className="warning-messages">
                                                    This certification has expired
                                                </div>
                                            }
                                        </td>
                                        <td className="expiry-date-column">
                                            {isEditMode ?
                                                <DatePicker
                                                    locale="en-gb"
                                                    dateFormat="dd/MM/yyyy"
                                                    placeholderText="?"
                                                    selected={ic.expiryDate ? new Date(ic.expiryDate) : null}
                                                    onChange={date => this.updateCertFields(ic.certID, { expiryDate: date })}
                                                /> :
                                                (!!ic.expiryDate && moment(ic.expiryDate).format('DD/MM/YYYY'))
                                            }
                                        </td>
                                        <td className="dig-column">
                                            {!!ic.uploadGUID && <>
                                                <a href={`/api/upload/get/${ic.uploadGUID}?isInline=true`} target="_blank" className="btn btn-secondary btn-sm download-button">
                                                    <span className="fa-solid fa-eye" />{' '}
                                                    PDF
                                                </a>
                                            </>}

                                            {isEditMode && <>
                                                <label className="btn btn-secondary btn-sm upload-button">
                                                    {ic.isUploading ? <>
                                                        <Loader isInline />{' '}Uploading...
                                                    </> : <>
                                                        <span className="fa-solid fa-upload"></span>{' '}Upload PDF
                                                        <input
                                                            type="file"
                                                            accept="application/pdf"
                                                            onChange={e => this.uploadCertFile(null, ic.certID, e.target)}
                                                        />
                                                    </>}
                                                </label>

                                                <button className="btn btn-secondary btn-sm ms-1" onClick={() => this.editCredlyLink(ic.certID)}>
                                                    <span className="fa-solid fa-link" />{' '}
                                                    Credly Link
                                                </button>

                                            </>}

                                            {!isEditMode && !!ic.url &&
                                                <a href={ic.url} target="_blank" className="btn btn-secondary btn-sm ms-1">
                                                    <span className="fa-solid fa-link" />{' '}
                                                    Credly
                                                </a>
                                            }

                                        </td>
                                        {isEditMode &&
                                            <td className="actions-column">
                                                <button className="btn btn-danger btn-sm" onClick={e => this.confirmDeleteCert(ic.certID, ic.certName)}>
                                                    <span className="fa-solid fa-trash"></span>
                                                </button>

                                                {loginInfo.isAdmin &&
                                                    <button className={`btn btn-sm btn-secondary ${settings.numSkipped > 0 ? 'email-settings-btn-skipped' : ''}`} onClick={e => this.showEmailSettingsModal({ certID: ic.certID })} title={settings.numSkipped + ' email(s) skipped'}>
                                                        <span className="fa-solid fa-envelope"></span>
                                                    </button>
                                                }
                                            </td>
                                        }
                                    </tr>
                                );
                            })}
                            {isEditMode &&
                                <tr>
                                    <td className="3">

                                        {isAddingCert ?
                                            <Select
                                                autoFocus
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                isClearable={false}
                                                options={certs.filter(c => !instructor.certs.find(ic => ic.certID == c.id))}
                                                getOptionValue={c => c.id}
                                                getOptionLabel={c => c.name}
                                                onChange={c => this.addCert(c)}
                                                value={null}
                                                menuIsOpen
                                                onBlur={e => this.setState({ isAddingCert: false })}
                                            /> :
                                            <button className="btn btn-secondary btn-block" onClick={e => this.startAddingCert()}>
                                                <span className="fa-solid fa-plus"></span>{' '}
                                                Add another...
                                            </button>
                                        }
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>

                    {loginInfo.isVendorOrAdmin && <>
                        
                        <h2>Certification notes</h2>

                        <NotesTable
                            instructorID={instructor.id}
                            courseID={0}
                            certID={0}
                            showNewestFirst
                            allowShowAll
                            maxNum={5}
                            noteTypes={['Certification']}
                            newNoteType="Certification"
                        />

                    </>}

                </div>
            );
        }
        return null;
    }

}

export default Credentials;